import { LegalReviewData } from '../services/api'

interface Props {
  review: LegalReviewData
  onClose: () => void
}

const severityStyles: Record<string, { bg: string; color: string; border: string }> = {
  critical: { bg: 'rgba(248, 113, 113, 0.2)', color: '#f87171', border: 'rgba(248, 113, 113, 0.4)' },
  high: { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: 'rgba(251, 191, 36, 0.4)' },
  medium: { bg: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa', border: 'rgba(96, 165, 250, 0.4)' },
  low: { bg: 'rgba(148, 163, 184, 0.2)', color: '#94a3b8', border: 'rgba(148, 163, 184, 0.4)' },
  info: { bg: 'rgba(52, 211, 153, 0.2)', color: '#34d399', border: 'rgba(52, 211, 153, 0.4)' },
}

const assessmentConfig: Record<string, { bg: string; color: string; border: string; icon: string; label: string }> = {
  PASS: { bg: 'rgba(52, 211, 153, 0.15)', color: '#34d399', border: 'rgba(52, 211, 153, 0.3)', icon: 'fa-check-circle', label: 'This SOW has been reviewed and appears legally sound with no critical issues.' },
  NEEDS_REVISION: { bg: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: 'rgba(251, 191, 36, 0.3)', icon: 'fa-exclamation-circle', label: 'This SOW requires revisions to address identified legal or scope clarity issues.' },
  HIGH_RISK: { bg: 'rgba(248, 113, 113, 0.15)', color: '#f87171', border: 'rgba(248, 113, 113, 0.3)', icon: 'fa-exclamation-triangle', label: 'This SOW contains high-risk issues that must be addressed before use.' },
  ERROR: { bg: 'rgba(248, 113, 113, 0.15)', color: '#f87171', border: 'rgba(248, 113, 113, 0.3)', icon: 'fa-times-circle', label: 'An error occurred during the legal review.' },
}

function ScoreBar({ score, label }: { score: number; label: string }) {
  const pct = (score / 10) * 100
  const color = score >= 8 ? '#34d399' : score >= 5 ? '#fbbf24' : '#f87171'
  return (
    <div className="text-center">
      <div className="text-3xl font-bold mb-1" style={{ color }}>{score}<span className="text-lg" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>/10</span></div>
      <div className="w-full h-2 rounded-full mb-1" style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{label}</div>
    </div>
  )
}

export default function LegalReviewResults({ review, onClose }: Props) {
  const assessment = assessmentConfig[review.overall_assessment] || assessmentConfig.ERROR

  if (review.error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)' }} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="rounded-xl max-w-lg w-full p-6" style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
          <div className="text-center">
            <i className="fa-solid fa-times-circle text-4xl mb-4" style={{ color: '#f87171' }}></i>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#e2e8f0' }}>Legal Review Error</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{review.error}</p>
            <button onClick={onClose} className="px-5 py-2.5 rounded-lg text-sm font-medium transition" style={{ border: '1px solid rgba(100, 180, 255, 0.2)', color: 'rgba(148, 163, 184, 0.8)' }}>Close</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)' }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              <i className="fa-solid fa-gavel" style={{ color: '#a78bfa' }}></i>
            </div>
            <div>
              <h3 className="text-lg font-bold" style={{ color: '#e2e8f0' }}>Legal Review Results</h3>
              <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Reviewed by {review.model_used}</p>
            </div>
          </div>
          <button onClick={onClose} className="transition hover:opacity-80" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Overall Assessment */}
          <div className="rounded-xl p-5" style={{ background: assessment.bg, border: `1px solid ${assessment.border}` }}>
            <div className="flex items-start space-x-4">
              <i className={`fa-solid ${assessment.icon} text-2xl mt-0.5`} style={{ color: assessment.color }}></i>
              <div>
                <h4 className="text-lg font-bold mb-1" style={{ color: '#e2e8f0' }}>
                  {review.overall_assessment.replace(/_/g, ' ')}
                </h4>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{assessment.label}</p>
              </div>
            </div>
          </div>

          {/* Scope Assessment */}
          {review.scope_assessment && (
            <div className="rounded-xl p-5" style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <h4 className="text-base font-bold mb-4 flex items-center" style={{ color: '#e2e8f0' }}>
                <i className="fa-solid fa-bullseye mr-2" style={{ color: '#60a5fa' }}></i>
                Scope Clarity Assessment
              </h4>
              <div className="grid grid-cols-3 gap-6 mb-4">
                <ScoreBar score={review.scope_assessment.in_scope_clarity_score} label="In-Scope Clarity" />
                <ScoreBar score={review.scope_assessment.out_scope_clarity_score} label="Out-of-Scope Clarity" />
                <ScoreBar score={review.scope_assessment.completeness_score} label="Completeness" />
              </div>
              {review.scope_assessment.comments && (
                <p className="text-sm mt-3 pt-3" style={{ color: 'rgba(148, 163, 184, 0.8)', borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  {review.scope_assessment.comments}
                </p>
              )}
            </div>
          )}

          {/* Findings */}
          {review.findings && review.findings.length > 0 && (
            <div>
              <h4 className="text-base font-bold mb-3 flex items-center" style={{ color: '#e2e8f0' }}>
                <i className="fa-solid fa-search mr-2" style={{ color: '#a78bfa' }}></i>
                Findings ({review.findings.length})
              </h4>
              <div className="space-y-3">
                {review.findings.map((finding, idx) => {
                  const sev = severityStyles[finding.severity] || severityStyles.info
                  return (
                    <div key={idx} className="rounded-lg p-4" style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-semibold text-sm" style={{ color: '#e2e8f0' }}>{finding.title}</h5>
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded uppercase ml-3 flex-shrink-0" style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}>
                          {finding.severity}
                        </span>
                      </div>
                      <p className="text-xs mb-2" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        <i className="fa-solid fa-map-pin mr-1"></i>{finding.location}
                      </p>
                      <p className="text-sm mb-3" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>
                        {finding.description}
                      </p>
                      <div className="rounded p-3" style={{ background: 'rgba(52, 211, 153, 0.08)', border: '1px solid rgba(52, 211, 153, 0.15)' }}>
                        <p className="text-xs font-semibold mb-1" style={{ color: '#34d399' }}>
                          <i className="fa-solid fa-lightbulb mr-1"></i>Recommendation
                        </p>
                        <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{finding.recommendation}</p>
                        {finding.suggested_text && (
                          <div className="mt-2 p-2 rounded text-xs font-mono" style={{ background: 'rgba(30, 41, 59, 0.8)', color: '#e2e8f0', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                            {finding.suggested_text}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Liability Risks */}
          {review.liability_risks && review.liability_risks.length > 0 && (
            <div className="rounded-xl p-5" style={{ background: 'rgba(248, 113, 113, 0.08)', border: '1px solid rgba(248, 113, 113, 0.2)' }}>
              <h4 className="text-base font-bold mb-3 flex items-center" style={{ color: '#f87171' }}>
                <i className="fa-solid fa-shield-halved mr-2"></i>
                Liability Risks ({review.liability_risks.length})
              </h4>
              <div className="space-y-3">
                {review.liability_risks.map((risk, idx) => (
                  <div key={idx} className="rounded-lg p-4" style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(248, 113, 113, 0.15)' }}>
                    <h5 className="font-semibold text-sm mb-2" style={{ color: '#e2e8f0' }}>
                      {risk.risk_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </h5>
                    <p className="text-sm mb-2" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{risk.description}</p>
                    <p className="text-sm" style={{ color: '#34d399' }}>
                      <i className="fa-solid fa-wrench mr-1"></i>
                      <span className="font-medium">Mitigation:</span> {risk.mitigation}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No findings message */}
          {(!review.findings || review.findings.length === 0) && (!review.liability_risks || review.liability_risks.length === 0) && (
            <div className="text-center py-8">
              <i className="fa-solid fa-check-circle text-4xl mb-3" style={{ color: '#34d399' }}></i>
              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>No issues found during legal review.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
